import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import OnboardingBackground from '../../components/OnboardingBackground';
import OnboardingCardTemplate from '../../components/OnboardingCardTemplate';
import { Body } from '../../components/Text';
import AppHands from '../../assets/icons/app_hands.svg';
import DynamicLinkService from '../../services/link';
import { ILinkResponse, LinkType } from '../../services/link/types';
import { useAmplitude } from '../../providers/AmplitudeProvider';
import { onWebAppDynamicLinkLoaded } from '../../tracking/trackers';
import ShareLinkType from './ShareLinkType';
import ReferralLinkType from './ReferralLinkType';
import OrganizationLinkType from './OrganizationLinkType';
import MagicAuthLinkType from './MagicAuthLinkType';

const DynamicLink = () => {
    const { isInitialized } = useAmplitude();
    const navigate = useNavigate();

    const params = useParams();
    const linkToken = params?.linkToken || params?.['*'];
    const [tokenData, setTokenData] = useState<ILinkResponse | undefined>();

    useEffect(() => {
        if (linkToken)
            DynamicLinkService.getLink(linkToken)
                .then((response) => {
                    setTokenData(response);
                })
                .catch((e) => {
                    console.error('Error in loading link!', e);
                    setTokenData({
                        status: 'error',
                        error:
                            e?.response?.data?.error ||
                            'Error in loading link!',
                    });
                    navigate('/');
                });
    }, [linkToken]);

    useEffect(() => {
        if (isInitialized && tokenData) {
            onWebAppDynamicLinkLoaded({
                link_token: linkToken,

                status: tokenData.status,
                error: tokenData.error,

                link_type: tokenData.data?.link_type,
                user: {
                    id: tokenData.data?.user?.id,
                    mobile: tokenData.data?.user?.mobile,
                },
                metadata: tokenData.data?.metadata,
            });
        }
    }, [isInitialized, tokenData]);

    return (
        <>
            {!tokenData ? (
                <OnboardingBackground>
                    <OnboardingCardTemplate
                        content={
                            <div
                                style={{
                                    paddingTop: '25%',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <CircularProgress
                                    color={'primary'}
                                    style={{
                                        width: 24,
                                        height: 24,
                                    }}
                                />
                                <div style={{ margin: 5 }} />
                                <Body
                                    style={{
                                        fontSize: 14,
                                    }}
                                >
                                    Loading...
                                </Body>
                            </div>
                        }
                    />
                </OnboardingBackground>
            ) : tokenData.status === 'error' ? (
                <OnboardingBackground>
                    <OnboardingCardTemplate
                        title={tokenData.error}
                        content={
                            <>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <img
                                        src={AppHands}
                                        alt=""
                                        width={150}
                                        height={150}
                                        style={{ marginTop: 30 }}
                                    />
                                    <div
                                        style={{
                                            margin: 10,
                                            textAlign: 'center',
                                        }}
                                    >
                                        The link you are trying to access is
                                        expired. Please ask the sender to
                                        regenerate the link!
                                    </div>
                                </div>
                            </>
                        }
                        style={{
                            paddingTop: '5%',
                        }}
                    />
                </OnboardingBackground>
            ) : (
                <>
                    {tokenData.data?.link_type === LinkType.SHARE_CATEGORY ||
                    tokenData.data?.link_type === LinkType.JOIN_ACCOUNT ? (
                        <ShareLinkType
                            linkToken={linkToken!}
                            tokenData={tokenData.data}
                        />
                    ) : tokenData.data?.link_type === LinkType.REFERRAL ? (
                        <ReferralLinkType
                            linkToken={linkToken!}
                            tokenData={tokenData.data}
                        />
                    ) : tokenData.data?.link_type ===
                      LinkType.JOIN_ORGANIZATION ? (
                        <OrganizationLinkType
                            linkToken={linkToken!}
                            tokenData={tokenData.data}
                        />
                    ) : tokenData.data?.link_type === LinkType.MAGIC_AUTH ? (
                        <MagicAuthLinkType
                            linkToken={linkToken!}
                            tokenData={tokenData.data}
                        />
                    ) : (
                        <OnboardingBackground>
                            <OnboardingCardTemplate
                                title="Link is not supported!"
                                content={<></>}
                            />
                        </OnboardingBackground>
                    )}
                </>
            )}
        </>
    );
};

export default DynamicLink;
